/**
 * FallbackEditorModal — edit one image in the brand-banner fallback pool.
 *
 * Everything destructive or fiddly lives here rather than on the card, so the
 * grid stays a glanceable list: the card carries only the enable/disable
 * switch (the reversible action) and an Edit button.
 *
 * Hard delete sits behind a SECOND modal, not `window.confirm` — the shared
 * `Modal` already tracks nesting depth for Escape, so a confirm layered over
 * this one behaves correctly.
 */
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { BannerPreview } from './BannerPreview';
import {
  BRAND_BANNER_FALLBACK_ACCEPT,
  deleteBrandBannerFallbackObject,
  uploadBrandBannerFallback,
} from '../../services/firebase';
import {
  removeBrandBannerFallback,
  updateBrandBannerFallback,
  type BrandBannerFallbackImage,
} from '../../services/brandBannerFallbacks';

interface FallbackEditorModalProps {
  image: BrandBannerFallbackImage;
  onClose: () => void;
}

export function FallbackEditorModal({ image, onClose }: FallbackEditorModalProps) {
  const [label, setLabel] = useState(image.label ?? '');
  const [creditUrl, setCreditUrl] = useState(image.creditUrl ?? '');
  const [enabled, setEnabled] = useState(image.enabled);
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [replaceProgress, setReplaceProgress] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The parent hands us the LIVE row, so a Replace (or another admin's edit)
  // repaints the preview on its own. The checkbox is local state, though, so it
  // needs an explicit re-sync; the label/credit drafts deliberately do NOT,
  // since the admin may be mid-edit.
  useEffect(() => {
    setEnabled(image.enabled);
  }, [image.enabled]);

  const busy = saving || replacing || deleting;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateBrandBannerFallback(image.id, {
        enabled,
        label: label.trim(),
        creditUrl: creditUrl.trim(),
      });
      toast.success('Saved');
      onClose();
    } catch (err) {
      console.error('Failed to save banner fallback:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Swap the picture, keeping the same row id.
   *
   * The old object is deleted only AFTER the RTDB row points at the new one —
   * if the upload fails the row still resolves, and if the delete fails we have
   * an orphan rather than a broken banner. When the new file has a different
   * extension the path differs, so the delete is a real cleanup; when it
   * matches, Storage has already overwritten in place and the delete is skipped.
   */
  const handleReplace = async (file: File) => {
    setReplacing(true);
    setReplaceProgress(0);
    const previousPath = image.storagePath;
    try {
      const { storagePath, downloadUrl } = await uploadBrandBannerFallback(
        image.id,
        file,
        { onProgress: setReplaceProgress },
      );
      await updateBrandBannerFallback(image.id, {
        url: downloadUrl,
        storagePath,
        filename: file.name,
      });
      if (previousPath && previousPath !== storagePath) {
        await deleteBrandBannerFallbackObject(previousPath);
      }
      toast.success('Image replaced');
    } catch (err) {
      console.error('Failed to replace banner fallback image:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to replace image');
    } finally {
      setReplacing(false);
      setReplaceProgress(0);
    }
  };

  /**
   * Drop the RTDB key first, then the bytes.
   *
   * That order is deliberate: once the key is gone RN can't serve the image,
   * which is the point of the action. A failed object delete leaves an orphan
   * in Storage — reported honestly in the toast rather than passed off as a
   * clean delete — and never leaves a live row pointing at deleted bytes.
   */
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await removeBrandBannerFallback(image.id);
      const storageDeleted = await deleteBrandBannerFallbackObject(image.storagePath);
      if (storageDeleted) {
        toast.success('Image deleted');
      } else {
        toast.success('Image removed (stored file was already missing)');
      }
      setConfirmingDelete(false);
      onClose();
    } catch (err) {
      console.error('Failed to delete banner fallback:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
      setDeleting(false);
    }
  };

  return (
    <>
      <Modal isOpen onClose={busy ? () => {} : onClose} title="Edit fallback banner" size="lg">
        <div className="p-4 space-y-4">
          <BannerPreview url={image.url} alt={image.filename || image.id} />

          <div className="text-xs text-gray-500 break-all">
            {image.filename || '(no filename)'}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={busy}
              placeholder="e.g. Watchmaker's bench"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Admin-facing caption. RN may render it over the banner.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Credit URL <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              type="url"
              value={creditUrl}
              onChange={(e) => setCreditUrl(e.target.value)}
              disabled={busy}
              placeholder="https://unsplash.com/photos/…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Attribution source. RN already shows “Image: {'{hostname}'}” under bespoke
              brand heroes and can do the same here.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={busy}
              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            Enabled — included in the pool RN picks from
          </label>

          <div className="border-t pt-4">
            <input
              ref={fileInputRef}
              type="file"
              accept={BRAND_BANNER_FALLBACK_ACCEPT}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleReplace(file);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              {replacing ? `Replacing… ${replaceProgress}%` : 'Replace image'}
            </Button>
            <p className="mt-1 text-xs text-gray-500">
              Keeps the same slot — brands already showing this banner keep showing
              it, with the new picture.
            </p>
          </div>

          <div className="border-t pt-4 flex items-center justify-between">
            <Button
              variant="danger"
              size="sm"
              onClick={() => setConfirmingDelete(true)}
              disabled={busy}
            >
              Delete permanently
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={handleSave} loading={saving} disabled={busy}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={confirmingDelete}
        onClose={deleting ? () => {} : () => setConfirmingDelete(false)}
        title="Delete this banner permanently?"
        size="sm"
      >
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-700">
            This removes the image from the pool <strong>and deletes the stored
            file</strong>. It can’t be undone — you’d have to re-upload it.
          </p>
          <p className="text-sm text-gray-700">
            To take it out of rotation without losing it, cancel and untick
            <strong> Enabled</strong> instead.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setConfirmingDelete(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleting}>
              Delete permanently
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
